import type { TechnicalDocumentType } from "./technical-document-review.js";

/**
 * SOLICITAÇÃO DE CORREÇÃO POR ITEM — o Engenheiro/PLH não escreve "corrigir
 * relatório": aponta SEÇÃO, ITEM/CAMPO, observação e prioridade. Cada item
 * vira uma pendência própria, com destino exato (deep link) para o Inspetor
 * responsável. Vale para qualquer documento técnico (RI, LP, ME, PM, US…).
 */

export type TechnicalCorrectionPriority = "NORMAL" | "ALTA" | "CRÍTICA";

export const TECHNICAL_CORRECTION_PRIORITY_LABELS: Record<TechnicalCorrectionPriority, string> = {
  NORMAL: "Normal",
  ALTA: "Alta",
  CRÍTICA: "Crítica"
};

/** Peso para a fila priorizada: CRÍTICA primeiro. */
export function priorityWeight(priority: TechnicalCorrectionPriority | null | undefined): number {
  return priority === "CRÍTICA" ? 3 : priority === "ALTA" ? 2 : priority === "NORMAL" ? 1 : 0;
}

export function highestPriority(priorities: ReadonlyArray<TechnicalCorrectionPriority | null | undefined>): TechnicalCorrectionPriority | null {
  let best: TechnicalCorrectionPriority | null = null;
  for (const p of priorities) if (p && priorityWeight(p) > priorityWeight(best)) best = p;
  return best;
}

export type TechnicalCorrectionStatus = "PENDENTE" | "CORRIGIDA" | "CANCELADA";

export const TECHNICAL_CORRECTION_STATUS_LABELS: Record<TechnicalCorrectionStatus, string> = {
  PENDENTE: "Pendente",
  CORRIGIDA: "Corrigida",
  CANCELADA: "Cancelada"
};

export interface TechnicalCorrectionActor {
  userId: string;
  professionalId: string | null;
  name: string;
}

export interface TechnicalCorrectionRequest {
  id: string;
  documentType: TechnicalDocumentType;
  documentId: string;
  documentNumber: string;
  revision: string;
  /** Ciclo de revisão central (LP/ME/PM…) — nulo para documentos com workflow próprio (RI). */
  reviewId: string | null;
  /** Ordem dentro do pedido ("01", "02"…). */
  sequence: string;
  sectionId: string;
  sectionLabel: string;
  itemId: string;
  itemLabel: string;
  fieldId: string;
  /** Rota completa que abre o documento na seção/item exatos. */
  anchor: string;
  comment: string;
  priority: TechnicalCorrectionPriority;
  requestedBy: TechnicalCorrectionActor;
  assignedTo: TechnicalCorrectionActor;
  createdAt: string;
  status: TechnicalCorrectionStatus;
  resolvedAt: string | null;
  resolvedBy: TechnicalCorrectionActor | null;
  resolutionComment: string;
}

/** O que o modal "SOLICITAR CORREÇÃO" entrega por linha. */
export interface TechnicalCorrectionItemInput {
  sectionId: string;
  itemId?: string;
  fieldId?: string;
  comment: string;
  priority?: TechnicalCorrectionPriority;
  /** Rótulos opcionais — quando ausentes, saem do catálogo de seções ou do próprio id. */
  sectionLabel?: string;
  itemLabel?: string;
}

export const CORRECTION_SECTION_REQUIRED = "Selecione a seção do documento que deve ser corrigida.";
export const CORRECTION_COMMENT_REQUIRED = "Informe a observação do Engenheiro para cada item de correção.";
export const CORRECTION_ITEMS_REQUIRED = "Adicione pelo menos um item para correção.";
export const CORRECTION_ITEM_REQUIRED = "Identifique o item / campo do documento que deve ser corrigido.";

/**
 * `requireItem`: MANDAR PARA CORREÇÃO nos END (mecanismo central de revisão
 * PLH) exige SEÇÃO + ITEM/CAMPO + OBSERVAÇÃO em cada linha — o Inspetor
 * recebe navegação direta ao item. As devoluções do RI aceitam item de seção
 * inteira (ex.: "Conclusão"), por isso o padrão continua sem essa exigência.
 */
export function validateCorrectionItems(items: ReadonlyArray<TechnicalCorrectionItemInput>, options: { requireItem?: boolean } = {}): string[] {
  const errors: string[] = [];
  if (!items || items.length === 0) return [CORRECTION_ITEMS_REQUIRED];
  items.forEach((item, index) => {
    const n = String(index + 1).padStart(2, "0");
    if (!item.sectionId || !item.sectionId.trim()) errors.push(`${n}: ${CORRECTION_SECTION_REQUIRED}`);
    if (options.requireItem && !(item.itemId && item.itemId.trim()) && !(item.fieldId && item.fieldId.trim())) errors.push(`${n}: ${CORRECTION_ITEM_REQUIRED}`);
    if (!item.comment || !item.comment.trim()) errors.push(`${n}: ${CORRECTION_COMMENT_REQUIRED}`);
    if (item.priority && !(item.priority in TECHNICAL_CORRECTION_PRIORITY_LABELS)) errors.push(`${n}: prioridade inválida.`);
  });
  return errors;
}

export interface CorrectionSectionDefinition {
  id: string;
  label: string;
  /** Como o item é chamado nesta seção ("Ponto", "Recomendação", "Item"). */
  itemNoun: string;
}

const NDT_SECTIONS: CorrectionSectionDefinition[] = [
  { id: "general", label: "Dados Gerais", itemNoun: "Campo" },
  { id: "parameters", label: "Parâmetros do Ensaio", itemNoun: "Parâmetro" },
  { id: "equipment", label: "Equipamentos / Certificados", itemNoun: "Equipamento" },
  { id: "consumables", label: "Consumíveis", itemNoun: "Consumível" },
  { id: "results", label: "Resultados", itemNoun: "Item" },
  { id: "croqui", label: "Croqui", itemNoun: "Região" },
  { id: "photos", label: "Fotos", itemNoun: "Foto" },
  { id: "observations", label: "Observações / Parecer", itemNoun: "Campo" },
  { id: "signatories", label: "Signatários", itemNoun: "Signatário" }
];

/** Seções elegíveis por tipo de documento — o modal só oferece o que existe no documento. */
export const CORRECTION_SECTIONS: Record<TechnicalDocumentType, CorrectionSectionDefinition[]> = {
  INTEGRITY: [
    { id: "general", label: "Dados Gerais", itemNoun: "Campo" },
    { id: "checklist", label: "Checklist Técnico", itemNoun: "Componente" },
    { id: "recommendations", label: "Recomendações", itemNoun: "Recomendação" },
    { id: "ndt", label: "END (Ensaios)", itemNoun: "Ensaio" },
    { id: "thickness", label: "Espessuras / AVR", itemNoun: "Ponto" },
    { id: "croqui", label: "Croqui", itemNoun: "Região" },
    { id: "photos", label: "Fotos", itemNoun: "Foto" },
    { id: "conclusion", label: "Conclusão", itemNoun: "Campo" }
  ],
  LP: NDT_SECTIONS,
  ME: NDT_SECTIONS.map(s => (s.id === "results" ? { ...s, label: "Pontos de Medição", itemNoun: "Ponto" } : s)),
  PM: NDT_SECTIONS,
  BSCAN: NDT_SECTIONS,
  USC: NDT_SECTIONS.map(s => (s.id === "results" ? { ...s, label: "Juntas / Resultados", itemNoun: "Junta" } : s)),
  AVR: [
    { id: "sources", label: "Fontes", itemNoun: "Relatório" },
    { id: "history", label: "Histórico por Ponto", itemNoun: "Ponto" },
    { id: "results", label: "Resultados", itemNoun: "Ponto" },
    { id: "analysis", label: "Análise / Conclusão", itemNoun: "Campo" }
  ]
};

export function sectionLabelOf(documentType: TechnicalDocumentType, sectionId: string): string {
  return CORRECTION_SECTIONS[documentType]?.find(s => s.id === sectionId)?.label || sectionId;
}

/**
 * DEEP LINK: rota do documento + seção + item + campo. O sistema abre o
 * documento correto, na seção correta, no item correto — nunca só a página
 * inicial. Para recomendação de Checklist, a rota é o Checklist da inspeção
 * (`?section=recommendations&item=REC-003`), que já destaca a Recommendation.
 */
export function buildCorrectionAnchor(documentRoute: string, target: { sectionId: string; itemId?: string | null; fieldId?: string | null }): string {
  const [pathAndQuery, hash] = documentRoute.split("#");
  const [path, existing] = (pathAndQuery || "").split("?");
  const params = new URLSearchParams(existing || "");
  params.set("section", target.sectionId);
  if (target.itemId) params.set("item", target.itemId); else params.delete("item");
  if (target.fieldId) params.set("field", target.fieldId); else params.delete("field");
  // Compatibilidade com o deep link já existente do Checklist (?recommendation=<id>).
  if (target.sectionId === "recommendations" && target.itemId) params.set("recommendation", target.itemId);
  return `${path}?${params.toString()}${hash ? `#${hash}` : ""}`;
}

export interface DeepLinkTarget {
  sectionId: string | null;
  itemId: string | null;
  fieldId: string | null;
}

/** Lê `?section=&item=&field=` (ou `?recommendation=`) de uma query string. */
export function parseDeepLinkTarget(search: string): DeepLinkTarget {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const rec = params.get("recommendation");
  return {
    sectionId: params.get("section") || (rec ? "recommendations" : null),
    itemId: params.get("item") || rec || null,
    fieldId: params.get("field") || null
  };
}

/** Texto da notificação ao Inspetor: "Relatório RI-2026-0015 possui uma correção pendente na Recomendação REC-003." */
export function correctionNotificationMessage(documentNumber: string, sectionLabel: string, itemNoun: string, itemLabel: string): string {
  const startsWithNoun = !!itemLabel && itemLabel.toLowerCase().startsWith(itemNoun.toLowerCase());
  const prefix = itemNoun.toLowerCase() === "campo" ? "no campo" : itemNoun === "Recomendação" ? "na Recomendação" : `em ${itemNoun}`;
  const where = itemLabel ? `${startsWithNoun ? prefix.split(" ")[0] : prefix} ${itemLabel}` : `na seção ${sectionLabel}`;
  return `Relatório ${documentNumber} possui uma correção pendente ${where}.`;
}

/** "Há 2 horas", "Há 1 dia", "Há 5 dias" — indicador de backlog. */
export function relativeTimeLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diffMs = Math.max(0, now.getTime() - then);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Agora";
  if (minutes < 60) return `Há ${minutes} minuto${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Há ${hours} hora${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Há ${days} dia${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Há ${months} ${months === 1 ? "mês" : "meses"}`;
  const years = Math.floor(months / 12);
  return `Há ${years} ano${years === 1 ? "" : "s"}`;
}
